/**
 * HamburgerMenu — slide-in drawer for the AppShell navigation.
 *
 * Phase 1 hand-authored (matches the Phase-1-lean style of button.tsx / card.tsx
 * etc.); position-fixed panel + backdrop + transform animation. No new runtime
 * deps. Phase 2+ can swap to ``npx shadcn add sheet`` for richer animations.
 *
 * Role-gated nav: User Management link only shows when the current user has
 * ``user.list`` permission (Content Admin / Super-admin / HR Manager). Audit Log
 * shows for everyone authenticated; the backend already scopes per AUD-03.
 */
import { useEffect, useState } from "react";
import { NavLink } from "react-router";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useLogout } from "@/features/auth/useLogout";
import { useAuthStore } from "@/lib/auth";
import { canMutate } from "@/lib/permissions";
import { TOKENS } from "@/lib/tokens";

const ROLES_WITH_USER_LIST = new Set(["content_admin", "super_admin", "hr_manager"]);

export function HamburgerMenu() {
  const { t } = useTranslation([
    "users",
    "audit",
    "auth",
    "positions",
    "templates",
    "question-bank",
    "admin-settings",
  ]);
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const canSeeUsers = user ? ROLES_WITH_USER_LIST.has(user.role) : false;
  const canSeeQuestionBank = user?.role === "content_admin";
  // Plan 03-08 G8 (IMP-039): content_admin writes consent versions, super_admin
  // reads them — both reach /admin/settings; hr_*/hiring_manager do not.
  const canSeeSettings =
    canMutate("consent_version", "write", user?.role) ||
    user?.role === "super_admin";
  // Plan 04-08 G8 (IMP-039): the HM finalist surface is hiring_manager-only — mirrors
  // backend admin_hm._require_hm. This is the entry-point for /finalists.
  const canSeeFinalists = user?.role === "hiring_manager";
  // Plan 04-09 G8 (IMP-039): the cross-domain overview is super_admin-only — mirrors
  // backend admin_overview. This is the entry-point for /overview (OPS-05).
  const canSeeOverview = user?.role === "super_admin";
  // BUG-091 (2026-06-05): the hiring_manager has a finalist-only surface — its menu is
  // thin (Finalistas + logout). The admin position tree (Posiciones, Templates) and the
  // Audit log are hidden; their routes are also RequireRole-guarded (defense in depth).
  const isHiringManager = user?.role === "hiring_manager";

  async function handleLogout() {
    setOpen(false);
    await logout();
  }

  const linkBase =
    "block px-4 py-3 rounded-md text-base transition-colors";
  const linkInactive = { color: TOKENS.text };
  const linkActive = {
    backgroundColor: TOKENS.primary,
    color: TOKENS.primaryFg,
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen(true)}
        aria-label={t("users:nav.menu_label")}
        aria-expanded={open}
        aria-controls="hamburger-drawer"
        style={{ minHeight: "44px", minWidth: "44px" }}
      >
        <Menu aria-hidden="true" />
      </Button>

      {open && (
        <div
          aria-hidden="true"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 40,
          }}
        />
      )}

      <aside
        id="hamburger-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t("users:nav.menu_label")}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(320px, 85vw)",
          backgroundColor: TOKENS.surfaceElevated,
          boxShadow: TOKENS.shadow,
          borderLeft: `1px solid ${TOKENS.border}`,
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 200ms ease",
          zIndex: 50,
          padding: "16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <span style={{ color: TOKENS.text, fontWeight: 600 }}>
            {t("users:nav.menu_label")}
          </span>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setOpen(false)}
            aria-label={t("users:nav.close")}
            style={{ minHeight: "44px", minWidth: "44px" }}
          >
            <X aria-hidden="true" />
          </Button>
        </div>

        <nav className="flex-1 space-y-1">
          {canSeeFinalists && (
            <NavLink
              to="/finalists"
              className={linkBase}
              style={({ isActive }) => (isActive ? linkActive : linkInactive)}
              onClick={() => setOpen(false)}
            >
              {t("hm:nav.finalists")}
            </NavLink>
          )}
          {canSeeOverview && (
            <NavLink
              to="/overview"
              className={linkBase}
              style={({ isActive }) => (isActive ? linkActive : linkInactive)}
              onClick={() => setOpen(false)}
            >
              {t("overview:nav.overview")}
            </NavLink>
          )}
          {!isHiringManager && (
            <NavLink
              to="/positions"
              className={linkBase}
              style={({ isActive }) => (isActive ? linkActive : linkInactive)}
              onClick={() => setOpen(false)}
            >
              {t("positions:nav.positions")}
            </NavLink>
          )}
          {!isHiringManager && (
            <NavLink
              to="/templates"
              className={linkBase}
              style={({ isActive }) => (isActive ? linkActive : linkInactive)}
              onClick={() => setOpen(false)}
            >
              {t("templates:nav.templates")}
            </NavLink>
          )}
          {canSeeQuestionBank && (
            <NavLink
              to="/question-bank"
              className={linkBase}
              style={({ isActive }) => (isActive ? linkActive : linkInactive)}
              onClick={() => setOpen(false)}
            >
              {t("question-bank:nav.question_bank")}
            </NavLink>
          )}
          {canSeeUsers && (
            <NavLink
              to="/admin/users"
              className={linkBase}
              style={({ isActive }) =>
                isActive ? linkActive : linkInactive
              }
              onClick={() => setOpen(false)}
            >
              {t("users:nav.users")}
            </NavLink>
          )}
          {!isHiringManager && (
            <NavLink
              to="/admin/audit"
              className={linkBase}
              style={({ isActive }) => (isActive ? linkActive : linkInactive)}
              onClick={() => setOpen(false)}
            >
              {t("audit:nav.audit")}
            </NavLink>
          )}
          {canSeeSettings && (
            <NavLink
              to="/admin/settings"
              className={linkBase}
              style={({ isActive }) => (isActive ? linkActive : linkInactive)}
              onClick={() => setOpen(false)}
            >
              {t("admin-settings:page.title")}
            </NavLink>
          )}
        </nav>

        <Button
          type="button"
          variant="outline"
          onClick={handleLogout}
          className="w-full"
          style={{ minHeight: "44px" }}
        >
          {t("auth:logout")}
        </Button>
      </aside>
    </>
  );
}
